import React, { useEffect, useState } from "react";
import {
  Paper,
  Text,
  Button,
  Group,
  Badge,
  Center,
  Stack,
} from "@mantine/core";
import { IconExternalLink, IconCheck, IconRocket } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useTemplate } from "../../../context/TemplateContext";
import { useSalon } from "../../../context/SalonContext";
import { BusinessInfo } from "../../../types/context";
import { isProductionEnvironment } from "../utils/FeatureLocks";

// Color constants
const COLOR_BACKGROUND_LIGHT = "#f8f9fa";
const COLOR_BORDER_LIGHT = "#e9ecef";

const WebsiteGeneratorTitlePage: React.FC = () => {
  const navigate = useNavigate();
  const { loadPreviewData } = useTemplate();
  const { businessInfo } = useSalon() as { businessInfo?: BusinessInfo | null };
  const [isPublished, setIsPublished] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isProduction = isProductionEnvironment();

  // Fetch publish status on mount
  useEffect(() => {
    const fetchStatus = async () => {
      if (!businessInfo?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const data = await loadPreviewData(businessInfo.id);
        setIsPublished(data?.metadata?.is_published || false);
      } catch (error) {
        console.error("Error fetching publish status:", error);
        setIsPublished(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatus();
  }, [businessInfo?.id, loadPreviewData]);

  const handleGetStarted = () => {
    navigate("/admin/website-generator-wizard");
  };

  return (
    <Paper p="md" style={{ marginBottom: "20px" }}>
      <Group justify="space-between" align="flex-start" mb="md">
        <Text size="xl" fw={700} mb="xs">
          Your Website
        </Text>
        {!isLoading && (
          <Badge
            color={isPublished ? "green" : "yellow"}
            variant="light"
            leftSection={
              isPublished ? (
                <IconCheck size={12} />
              ) : (
                <IconExternalLink size={12} />
              )
            }
          >
            {isPublished ? "Published" : "Not Published"}
          </Badge>
        )}
      </Group>

      <Text c="dimmed" mb="lg">
        Manage the design, content, and layout of your site.
      </Text>

      {/* Website Preview Area */}
      <Paper
        p="xl"
        style={{
          backgroundColor: COLOR_BACKGROUND_LIGHT,
          border: `1px solid ${COLOR_BORDER_LIGHT}`,
          borderRadius: "8px",
          minHeight: "300px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Center>
          {isProduction ? (
            <Stack align="center" gap="md">
              <IconRocket size={48} color="#6c757d" />
              <Text size="xl" fw={600} c="dimmed">
                Coming Soon
              </Text>
              <Text size="md" c="dimmed" ta="center" maw={400}>
                We're working hard to bring you a powerful website builder. Stay
                tuned for updates!
              </Text>
              <Badge color="blue" variant="light" size="lg">
                In Development
              </Badge>
            </Stack>
          ) : (
            <Stack align="center" gap="md">
              <Text size="lg" fw={500}>
                Set-up your website in five minutes.
              </Text>
              <Button color="blue" size="md" onClick={handleGetStarted}>
                Get Started
              </Button>
            </Stack>
          )}
        </Center>
      </Paper>
    </Paper>
  );
};

export default WebsiteGeneratorTitlePage;
